import React, { useState, useEffect } from "react";
import { usePreference } from "../../context/PreferenceContext";
import { Table, Input, Button, Space, message, Popconfirm, Avatar, Modal, Descriptions, Tag, Divider, Col, Select } from "antd";
import {
  ReloadOutlined,
  UndoOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import AddProductOption from "../../component/AddProductOption";
import ProductOptionServ from "../../service/ProductOptionServ";
import ProductServ from "../../service/ProductServ";
import ShopServ from "../../service/ShopServ";
import CategoryServ from "../../service/CategoryServ";
import SubCategoryServ from "../../service/SubCategoryServ";
import "../../css/productOption.css";
import "../../css/shops.css";
import GlobalButton from "../../util/GlobalButton";
import EmptyState from "../../util/EmptyState";
import Breadcrumbs from "../../component/Breadcrumbs";
import { FilterBar } from "../../component/ui/FilterBar";
import DataTable from "../../component/ui/DataTable";
import SearchInput from "../../component/ui/SearchInput";
import { PrimaryButton, ResetButton } from "../../component/ui/ActionButtons";
import { getImageUrl } from "../../component/SafeImage";

const ProductOptions = () => {
  const { formatPrice } = usePreference();
  const [searchText, setSearchText] = useState("");
  const [productOptions, setProductOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addProductOption, setAddProductOption] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [productOptionDetails, setProductOptionDetails] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalProductOption, setTotalProductOption] = useState(0);
  const [selectedProductDetail, setSelectedProductDetail] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { Option } = Select;
  const [allCategories, setAllCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);
  const [allShops, setAllShops] = useState([]);

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedShop, setSelectedShop] = useState(null);

  // ✅ Fetch data on mount
  useEffect(() => {
    const fetchAllShops = async () => {
      try {
        const response = await ShopServ.getSellerShops();
        setAllShops(Array.isArray(response) ? response : response?.data || response?.shops || []);
      } catch (error) {
        console.error("Error fetching shops:", error);
      }
    };
    
    const fetchCategories = async () => {
      try {
        const response = await CategoryServ.index({ limit: 1000, filter: JSON.stringify({ parentId: null }) });
        setAllCategories(Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    const fetchSubCategories = async () => {
      try {
        const response = await SubCategoryServ.index({ limit: 2000 });
        setAllSubCategories(Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : []);
      } catch (error) {
        console.error("Error fetching subcategories:", error);
      }
    };

    fetchAllShops();
    fetchCategories();
    fetchSubCategories();
  }, []);

  // ✅ Filter subcategories when category changes
  useEffect(() => {
    if (!selectedCategory || selectedCategory === "All") {
      setFilteredSubCategories([]);
      setSelectedSubCategory(null);
      return;
    }

    const cat = allCategories.find(c => c.name === selectedCategory);
    if (cat) {
      const catId = cat._id || cat.id;
      const subs = allSubCategories.filter(s => {
        const pId = s.parentId?._id || s.parentId;
        const cId = s.categoryId?._id || s.categoryId;
        return pId === catId || cId === catId;
      });
      setFilteredSubCategories(subs);
      setSelectedSubCategory(null);
    }
  }, [selectedCategory, allCategories, allSubCategories]);

  // ✅ Fetch all products and extract their options/variants
  const fetchProductOptions = async () => {
    setLoading(true);
    try {
      const response = await ProductServ.getSellerProducts({ limit: "all" });
      const products = response?.data || [];

      const flattened = [];
      products.forEach(p => {
        if (p.options && p.options.length > 0) {
          p.options.forEach((opt, idx) => {
            flattened.push({
              key: `${p._id}_${idx}`,
              _id: p._id, // Product ID for backend operations
              name: opt.name, // Option group name (e.g. Color)
              option: opt.variants?.map(v => v.value) || [], // Array of values
              productId: { _id: p._id, name: p.name },
              variantsCount: opt.variants?.length || 0,
              fullOptions: p.options, // All options of this product
              originalProduct: p
            });
          });
        }
      });

      setProductOptions(flattened);
      setTotalProductOption(flattened.length);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch product options");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Pagination handlers
  const handlePageChange = (page) => {
    setPage(page);
    const pageContent = document.querySelector('.page-content');
    if (pageContent) pageContent.scrollTo({ top: 0, behavior: 'smooth' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  const onShowSizeChange = (current, size) => setPageSize(size);

  // ✅ Fetch when page or pageSize changes
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchProductOptions();
    }, 500);
    return () => clearTimeout(handler);
  }, [page, pageSize]);

  // ✅ Add product option
  const handleAddProductOption = () => {
    setProductOptionDetails(null);
    setIsEditing(false);
    setAddProductOption(true);
  };

  // ✅ Edit product option
  const handleEditProductOption = (record) => {
    setProductOptionDetails({
      productId: record._id,
      options: record.fullOptions
    });
    setIsEditing(true);
    setAddProductOption(true);
  };

  // ✅ Delete product option group
  const handleDeleteProductOption = async (record) => {
    try {
      const updatedOptions = record.fullOptions.filter(opt => opt.name !== record.name);
      await ProductServ.update(record._id, { options: updatedOptions });
      message.success(`Option group "${record.name}" removed from ${record.productId.name}`);
      fetchProductOptions();
    } catch (error) {
      console.error(error);
      message.error("Failed to remove product option");
    }
  };

  // ✅ Show Quick View Modal
  const handleShowDetail = (optionRecord) => {
    setSelectedProductDetail(optionRecord);
    setShowDetailModal(true);
  };

  // ✅ Refresh after add/edit modal close
  const refreshProductOptions = async (isRefresh) => {
    setAddProductOption(false);
    if (isRefresh) fetchProductOptions();
  };

  // ✅ Search and Select filters
  const filteredData = productOptions.filter((item) => {
    const p = item.originalProduct;
    if (!p) return false;

    const matchSearch = item.name?.toLowerCase().includes(searchText.toLowerCase()) || 
                        p.name?.toLowerCase().includes(searchText.toLowerCase());
    const matchCategory = !selectedCategory || selectedCategory === "All" || p.category === selectedCategory;
    const matchSubCategory = !selectedSubCategory || p.subCategoryId === selectedSubCategory || p.subCategoryId?._id === selectedSubCategory;
    const matchShop = !selectedShop || selectedShop === "All" || p.shopId === selectedShop || p.shopId?._id === selectedShop;

    return matchSearch && matchCategory && matchSubCategory && matchShop;
  });

  // ✅ Table columns
  const columns = [
    {
      title: "Image",
      key: "image",
      width: 80,
      render: (_, record) => (
        <Avatar
          src={getImageUrl(record.originalProduct?.imageUrl || record.originalProduct?.images?.[0])}
          shape="square"
          size={50}
          className="pcu-border-slate-light"
        />
      ),
    },
    {
      title: "Product Name",
      dataIndex: "productId",
      key: "productId",
      render: (productId) => (
        <span className="pcu-font-600 pcu-color-title">
          {productId?.name || "N/A"}
        </span>
      ),
    },
    {
      title: "Price",
      key: "price",
      align: "right",
      render: (_, record) => {
        const p = record.originalProduct;
        const price = p?.options?.[0]?.variants?.[0]?.sellerPrice ||
                      p?.variants?.[0]?.sellerPrice ||
                      p?.sellerPrice ||
                      p?.price;
        return (
          <span style={{ color: '#FF8800', fontWeight: 600 }}>
            {formatPrice(price)}
          </span>
        );
      }
    },
    {
      title: "MRP",
      key: "mrp",
      width: 90,
      align: "right",
      render: (_, record) => {
        const p = record.originalProduct;
        const mrp = p?.options?.[0]?.variants?.[0]?.mrp ||
                    p?.variants?.[0]?.mrp ||
                    p?.mrp;
        return (
          <span style={{ color: '#888', textDecoration: 'line-through', fontSize: 12 }}>
            {mrp ? formatPrice(mrp) : 'N/A'}
          </span>
        );
      }
    },
    {
      title: "Variants",
      dataIndex: "variantsCount",
      key: "variantsCount",
      width: 120,
      render: (count) => <span className="pcu-font-600 pcu-color-orange">{count} items</span>
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleShowDetail(record);
          }}
          style={{
            borderRadius: 3,
            fontWeight: 600,
            background: '#ffffffff',
            color: '#004754ff',
            border: '1px solid #e4e4e7',
            boxShadow: '0 0 0 1px #e4e4e7',
            height: 'auto',
            padding: '0px 8px',
            fontSize: 13,
          }}
        >
          View
        </Button>
      )
    },
  ];

  return (
    <div className="ss-wrapper">
      <div className="ss-container">
        
        {/* Header Section */}
        <div className="ss-header">
          <div className="ss-header-left">
            <div className="ss-header-icon">
              <AppstoreOutlined style={{ fontSize: 22, color: '#4776E6' }} />
            </div>
            <div>
              <h1 className="ss-title">Product Options</h1>
              <p className="ss-subtitle">{totalProductOption} options listed</p>
            </div>
          </div>
        </div>

        {/* Filter Section */}
        <div className="ss-filter-bar">
          <div className="ss-filter-field" style={{ flex: 1, minWidth: '200px' }}>
            <span className="ss-filter-label">Search Options</span>
            <Input
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              placeholder="Search by option name..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="ss-search-input"
              allowClear
            />
          </div>

          <div className="ss-filter-field" style={{ flex: 1, minWidth: '150px' }}>
            <span className="ss-filter-label">Shop Name</span>
            <Select
              placeholder="All Shops"
              style={{ width: '100%', height: '36px' }}
              allowClear
              onChange={setSelectedShop}
              value={selectedShop}
            >
              <Option value="All">All Shops</Option>
              {allShops.map(s => <Option key={s._id || s.id} value={s._id || s.id}>{s.name}</Option>)}
            </Select>
          </div>

          <div className="ss-filter-field" style={{ flex: 1, minWidth: '150px' }}>
            <span className="ss-filter-label">Category</span>
            <Select
              placeholder="All Categories"
              style={{ width: '100%', height: '36px' }}
              allowClear
              onChange={setSelectedCategory}
              value={selectedCategory}
            >
              <Option value="All">All Categories</Option>
              {allCategories.map(c => <Option key={c._id} value={c.name}>{c.name}</Option>)}
            </Select>
          </div>

          <div className="ss-filter-field" style={{ flex: 1, minWidth: '150px' }}>
            <span className="ss-filter-label">Subcategory</span>
            <Select
              placeholder="All SubCategories"
              style={{ width: '100%', height: '36px' }}
              allowClear
              disabled={!selectedCategory || selectedCategory === "All"}
              onChange={setSelectedSubCategory}
              value={selectedSubCategory}
            >
              {filteredSubCategories.map(s => <Option key={s._id} value={s._id}>{s.name}</Option>)}
            </Select>
          </div>

          <div className="ss-filter-reset">
            <button
              className="ss-reset-btn"
              onClick={() => {
                setSearchText("");
                setSelectedCategory(null);
                setSelectedSubCategory(null);
                setSelectedShop(null);
                fetchProductOptions();
              }}
            >
              <UndoOutlined /> Reset
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="ss-table-wrap">
          <Table
            className="custom-table table-container"
            dataSource={filteredData}
            columns={columns}
            rowKey={(record) => record.id || record._id}
            loading={loading}
            pagination={{
              className: "custom-pagination pcu-padding-16",
              current: page,
              pageSize: pageSize,
              total: totalProductOption,
              onChange: handlePageChange,
              onShowSizeChange: onShowSizeChange,
            }}
            bordered={false}
            scroll={{ x: 'max-content' }}
            locale={{
              emptyText: (
                <EmptyState
                  title="No Product Options"
                  description={searchText ? "No options match your search." : "No options defined for products."}
                  gradient="purple"
                  showAction={false}
                  icon="inbox"
                />
              )
            }}
          />
        </div>

          <Modal
            open={showDetailModal}
            onCancel={() => setShowDetailModal(false)}
            footer={null}
            width={650}
            closable={true}
            className="ss-modal"
          >
            {selectedProductDetail && (
              <div className="ss-modal-body">
                <div className="ss-modal-hero" style={{ position: 'relative' }}>

                  <div className="ss-modal-img">
                    <img 
                      src={getImageUrl(selectedProductDetail.originalProduct?.imageUrl || selectedProductDetail.originalProduct?.images?.[0]) || 'https://via.placeholder.com/110?text=N/A'} 
                      alt={selectedProductDetail.productId?.name} 
                      style={{ width: 110, height: 110, objectFit: 'cover' }} 
                    />
                  </div>
                  <div className="ss-modal-info">
                    <h2 className="ss-modal-name">{selectedProductDetail.productId?.name}</h2>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#FF8800', marginBottom: 10 }}>
                      {(() => {
                        const p = selectedProductDetail.originalProduct;
                        const price = p?.options?.[0]?.variants?.[0]?.sellerPrice || p?.variants?.[0]?.sellerPrice || p?.sellerPrice || p?.price;
                        const mrp = p?.options?.[0]?.variants?.[0]?.mrp || p?.variants?.[0]?.mrp || p?.mrp;
                        return (
                          <>
                            {formatPrice(price)}
                            {mrp > price && (
                              <span style={{ fontSize: 13, color: '#9ca3af', textDecoration: 'line-through', marginLeft: 8, fontWeight: 500 }}>
                                {formatPrice(mrp)}
                              </span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    <div className="ss-modal-tags">
                      <span className="ss-tag ss-tag-blue">{selectedProductDetail.originalProduct?.category || "Category"}</span>
                      <span className="ss-tag ss-tag-orange">Option: {selectedProductDetail.name}</span>
                      <span className="ss-tag ss-tag-green">{selectedProductDetail.variantsCount} Variants</span>
                    </div>
                  </div>
                </div>

                <div className="ss-modal-details">
                  <div className="ss-modal-row">
                    <div className="ss-modal-label">Option Name</div>
                    <div className="ss-modal-value">{selectedProductDetail.name}</div>
                  </div>
                  <div className="ss-modal-row">
                    <div className="ss-modal-label">Values</div>
                    <div className="ss-modal-value">
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {selectedProductDetail.option?.map((v, i) => (
                          <span key={i} className="ss-tag ss-tag-blue" style={{ margin: 0, padding: '2px 8px' }}>{v}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="ss-modal-row">
                    <div className="ss-modal-label">Total Variants</div>
                    <div className="ss-modal-value">{selectedProductDetail.variantsCount} Items</div>
                  </div>
                  
                  <div className="ss-modal-row" style={{ borderTop: '1px solid #f3f4f6', marginTop: 10 }}>
                    <div className="ss-modal-label">Associated Shop</div>
                    <div className="ss-modal-value">
                      {selectedProductDetail.originalProduct?.shopId ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <img 
                            src={getImageUrl(selectedProductDetail.originalProduct?.shopId?.imageUrl || selectedProductDetail.originalProduct?.shopId?.image) || 'https://via.placeholder.com/36?text=N/A'} 
                            alt={selectedProductDetail.originalProduct?.shopId?.name} 
                            style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 8 }} 
                          />
                          <div>
                            <div style={{ fontWeight: 600, color: '#1a1d2e', fontSize: 13 }}>
                              {selectedProductDetail.originalProduct?.shopId?.name}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>Not available</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="ss-modal-footer" style={{ justifyContent: 'space-between' }}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => {
                      handleEditProductOption(selectedProductDetail);
                      setShowDetailModal(false);
                    }}
                    style={{ borderRadius: 8, color: '#4776E6', borderColor: '#4776E6', height: 38, fontWeight: 600 }}
                  >
                    Edit
                  </Button>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Popconfirm
                      title={`Remove "${selectedProductDetail.name}" from this product?`}
                      onConfirm={() => {
                        handleDeleteProductOption(selectedProductDetail);
                        setShowDetailModal(false);
                      }}
                      okText="Yes"
                      cancelText="No"
                    >
                      <button className="ss-modal-close-btn" style={{ background: '#fff1f0', color: '#ff4d4f', border: '1px solid #ffccc7' }}>
                        <DeleteOutlined style={{ marginRight: '6px' }} /> Remove Option Group
                      </button>
                    </Popconfirm>
                    <button className="ss-modal-close-btn" onClick={() => setShowDetailModal(false)}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Modal>

          {/* Add/Edit Modal */}
          {addProductOption && (
            <AddProductOption
              open={addProductOption}
              optionDetails={productOptionDetails}
              refreshOptions={refreshProductOptions}
              isEditing={isEditing}
            />
          )}
    </div>
    </div>
  );
};

export default ProductOptions;

